import { React, useState, useParams, useNavigate, SelectChangeEvent, Typography, Box, Divider, Grid2, IconButton, ArrowBackIosIcon, dayjs, Dayjs, useApi, useEffect, Alphanumeric_Regex } from '../../utils/globalImports';
import { AppLayout, CustomButton, CustomTextField, CustomDropdown, CustomRadioButton, CustomDatePicker, CustomAddFeeStageModal, validationMessages, Toaster, FilterCard, CustomDataTable } from '../../utils/globalImports';
import { ApiResponse } from '../../services/apiService';
import { useFormUnsavedChanges } from '../../hooks/useFormUnsavedChanges';
import { majorRoutes } from '../../constants/majorRoutes';
import { useConfirmCloseDialog } from '../../contexts/ConfirmCloseDialogContext';
import { validateField, validationPatterns } from '../../utils/validationUtils';
import { headingStyles } from '../../assets/styles/CommonStyles';

interface Option {
    value: string | number;
    text: string;
}

interface Framework {
    frameworkName: string;
    clientId: number;
    status?: boolean;
    startDate: Dayjs | string | null;
    endDate: Dayjs | string | null;
    feeStages?: FeeStage[];
    createdId?: number;
    updatedId?: number;
}

interface FeeStage {
    feeStageId: number;
    frameworkId: number;
    refrenceNumber: string;
    description: string;
    value: number;
    valuePer: string;
    completionNotes: string;
    status: boolean;
    // createdAt: Dayjs | string | null;
    //updatedAt: Dayjs | string | null;
}

const isValidDate = (dateString: Dayjs | string) => {
    return dayjs(dateString, 'DD-MM-YYYY', true).isValid();
};

const AddFramework: React.FC = () => {
    const { execute } = useApi();
    const navigate = useNavigate();
    const [selectedStatus, setSelectedStatus] = useState<any>([true]);
    const [client, setClient] = useState<Option[]>([]);
    const [open, setOpen] = useState(false);  // State to control modal visibility
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [selectedFramework, setSelectedFramework] = useState<Framework | null>(null);

    const [isOn, setIsOn] = useState<boolean>();  // State to manage the toggle button's "on/off" state
    const [dialogOpen, setDialogOpen] = useState(false);  // State to manage confirm dialog "open/close" state
    const [selectedFeeStage, setSelectedFeeStage] = useState<any | null>(null);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [feeStageDataList, setFeeStageDataList] = useState<any[]>([]);
    //   const [currentStatus, setcurrentStatus] = useState<Boolean[]>([true]);

    const { frameworkId } = useParams<{ frameworkId?: string }>(); // Get frameworkId from URL
    const props = { frameworkId, title: 'Fee Stage', actionText: 'Add Fee Stage', searchbarText: validationMessages.feeStage_searchText }

    const userId = Number(localStorage.getItem("userId"));
    const delay = (ms: number | undefined) => new Promise(resolve => setTimeout(resolve, ms));

    const [formData, setFormData] = useState<Framework>({
        frameworkName: '',
        clientId: -1,
        startDate: null, // Store as Dayjs or null
        endDate: null,   // Store as Dayjs or null
        status: true
    });

    const [initialFormData, setInitialFormData] = useState<Framework>({
        frameworkName: '',
        clientId: -1,
        startDate: null, // Store as Dayjs or null
        endDate: null,   // Store as Dayjs or null
        status: true
    });

    const [errors, setErrors] = useState({
        frameworkName: '',
        associatedClient: '',
        startDate: '',
        endDate: '',
    });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

    const { showConfirmCloseDialog } = useConfirmCloseDialog();

    useEffect(() => {
        if (frameworkId) {
            fetchFramework();
        } else {
            setFormData({
                frameworkName: '',
                clientId: -1,
                startDate: null,
                endDate: null,
                status: true,
            });
        }
        fetchClient(); // Fetch Associated Clients on component mount
    }, [frameworkId])

    const sortOptions = [
        { value: "referenceNumber", text: "Reference Number" },
         { value: "description", text: "Description" },
         { value: "value", text: "Value" },


    ];

    // Fetch framework
    const fetchFramework = async () => {
        const { data } = await execute<Framework>('get', `/Framework/GetFramework/${frameworkId}`);
        if (data) {
            setSelectedFramework(data)
            // Update formData with fetched data
            const formData: Framework = {
                frameworkName: data.frameworkName || '',
                clientId: data.clientId || -1,
                startDate: dayjs(data.startDate) || null,
                endDate: dayjs(data.endDate) || null,
                status: data.status
            };
            setFormData(formData);
            setInitialFormData(formData);
            setFeeStageDataList(data.feeStages ?? [])
            showGrid(data.feeStages?.filter((feestage) => feestage.status === true)); // Send updated list to showGrid
        }
        else {
            navigate('/404');  // Redirect to a Not Found page
        }
    };

    // fetch Associated Clients
    const fetchClient = async () => {
        const { data } = await execute('get', '/Client/GetAllClients');
        if (data && Array.isArray(data)) {
            const clientOptions = data
                .filter((client: any) => client.status === 1) // Filter active clients
                .map((client: any) => ({
                    value: client.clientId,  // Ensure `id` exists in the response
                    text: String(client.clientName).trim(),
                }))
                .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' })); // Case-insensitive sorting
            setClient(clientOptions);
        }
    }

    // fetch All FeeStage 
    const fetchAllFeeStages = async () => {
        const { data } = await execute<FeeStage[]>('get', `/Framework/GetFeeStagesByFrameworkId/${frameworkId}`);
        if (data && Array.isArray(data)) {
            setFeeStageDataList(data)
            showGrid(data,selectedStatus); // Send updated list to showGrid
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : type === "radio" ? value === "true" : value,
        }));
    };


    const handleNavigate = async (shouldDelay = false) => {
        if (shouldDelay) {
            await delay(2000); // Apply delay for 2 seconds if shouldDelay is true
        }
        navigate('/framework/frameworks');
    };

    // Handle opening the modal
    const handleOpen = () => {
        setOpen(true);
    };

    // Handle closing the modal
    const handleClose = (closeConfirmationRequired: boolean = true) => {
        if (hasUnsavedChanges && closeConfirmationRequired) {
            showConfirmCloseDialog({
                onConfirm: () => {
                    setHasUnsavedChanges(false);
                    setOpen(false);
                },
                onCancel: () => {
                },
            });
        }
        else {
            setOpen(false);
        }
    };

    // validate client form field
    const validate = () => {
        let isValid = true;
        const errors = { frameworkName: '', associatedClient: '', startDate: '', endDate: '' };

        errors.frameworkName = validateField(formData.frameworkName, {
            fieldName: "Framework name",
            required: true,
            maxLength: 100,
            pattern: validationPatterns.alphanumericWithHyphenSpace,
        });

        if (errors.frameworkName) isValid = false;

        errors.associatedClient = validateField(formData.clientId, {
            fieldName: "Associated client",
            required: true
        });

        if (errors.associatedClient) isValid = false;

        if (!formData.startDate) {
            errors.startDate = validationMessages.common_required("Start date");
            isValid = false;
        }
        else if (!isValidDate(formData.startDate)) {
            errors.startDate = validationMessages.common_invalidDate("Start date");
            isValid = false;
        }


        if (!formData.endDate) {
            errors.endDate = validationMessages.common_required("End date");
            isValid = false;
        }
        else if (!isValidDate(formData.endDate)) {
            errors.endDate = validationMessages.common_invalidDate("End date");
            isValid = false;
        }

        if (
            formData.startDate &&
            formData.endDate &&
            (dayjs(formData.endDate).isSame(dayjs(formData.startDate), 'day') ||
                dayjs(formData.endDate).isBefore(dayjs(formData.startDate), 'day'))
        ) {
            errors.endDate = validationMessages.framework_endDate;
            isValid = false;
        }

        setErrors(errors);
        return isValid;
    };

    // handle add or edit framework
    const handleFrameworkSubmit = async () => {
        if (validate()) {
            let response: ApiResponse<any>;
            if (frameworkId) {
                const updatedFramework = {
                    ...selectedFramework, // Keep the original user data
                    frameworkName: formData.frameworkName,
                    clientId: formData.clientId,
                    startDate: formData.startDate ? dayjs(formData.startDate).format("YYYY-MM-DD") : null,
                    endDate: formData.endDate ? dayjs(formData.endDate).format("YYYY-MM-DD") : null,
                    status: formData.status,
                    updatedId: userId,
                    feeStages: feeStageDataList
                };

                response = await execute('put', '/Framework/UpdateFramework', updatedFramework);

            } else {
                // Adding a new Framework
                const framework: Framework = {
                    frameworkName: formData.frameworkName,
                    clientId: formData.clientId,
                    startDate: formData.startDate ? dayjs(formData.startDate).format("YYYY-MM-DD") : null,
                    endDate: formData.endDate ? dayjs(formData.endDate).format("YYYY-MM-DD") : null,
                    createdId: userId,
                    feeStages: feeStageDataList
                };

                response = await execute('post', '/Framework/AddFramework', framework);
            }

            if (response!) {
                if (response.error) {
                    // If there's an error in the response, display the error message
                    setToast({ open: true, message: response.error, type: "error" });
                } else {
                    setInitialFormData(formData);
                    if (typeof response.data === 'object' && response.data.frameworkId) {
                        // If the response is successful, display the success message
                        // console.log(response.data.frameworkId)
                        setToast({ open: true, message: validationMessages.framework_InsertResponse_Text, type: "success" });
                        navigate(`/framework/add-framework/${response.data.frameworkId}`);
                    }
                    else {
                        setToast({ open: true, message: response.data, type: "success" });
                        // handleNavigate(true);
                    }
                }
            }
        }
    }

    // show fee stage data
    const showGrid = (updatedData: any, status = [true]) => {
        const data = updatedData
            .filter((feeStage: any) => status.includes(feeStage.status))
            .map((feestage: any) => {
                const value = `${feestage.value.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} ${feestage.valuePer}`;
                const Status = feestage.status ? "Active" : "Inactive";
                return { feeStageId: feestage.feeStageId, frameworkId: feestage.frameworkId, "referenceNumber": feestage.refrenceNumber, description: feestage.description, value: value, "Terms and Notes": feestage.completionNotes, Status }
            })
        setFilteredData(data)
    }

    // filter fee stage Data
    const handleSearchGridData = (
        query: string,
        status: Number,
        category?: any,
        region?: any,
        contactType?: any,
        date?: string,
        proposalType?: any,
        startDate?: string,
        endDate?: string
    ) => {
        const lowerCaseQuery = query.toLowerCase();
        // Map status strings to corresponding numbers
        const statusMapping: Record<string, number> = {
            true: 1,
            false: 0,
        };

        const filtered = feeStageDataList.filter((item) => {
            // Convert item.Status to its numeric equivalent or skip if it doesn't match
            const itemStatus = statusMapping[item.status] ?? -1;

            const matchesQuery =
                item.refrenceNumber.toLowerCase().includes(lowerCaseQuery) ||
                item.description.toLowerCase().includes(lowerCaseQuery);

            const matchesStatus =
                status === -1 || status === 2 || itemStatus === status;
            // 📅 Date Range filter
            let matchDateRange = true;

            if (startDate || endDate) {
                //const created = item.createdAt?.local().format("YYYY-MM-DD");
                //const updated = item.updatedAt?.local().format("YYYY-MM-DD");
                const created = item.createdAt ? dayjs(item.createdAt).local().format("YYYY-MM-DD") : null;
                const updated = item.updatedAt ? dayjs(item.updatedAt).local().format("YYYY-MM-DD") : null;

                const s = startDate ?? null;
                const e = endDate ?? null;

                const checkDateInRange = (d: string | null) => {
                    if (!d) return false;

                    if (s && e) return d >= s && d <= e;
                    if (s) return d >= s;
                    if (e) return d <= e;

                    return true;
                };

                matchDateRange =
                    checkDateInRange(created) || checkDateInRange(updated);
            }

            return matchesQuery && matchDateRange && matchesStatus;
        })

        const newStatus =
            status === 0
                ? [false]                          // When status is 0, set currentStatus to 'Inactive'
                : status === -1 || status === 2
                    ? [true, false]                // When status is -1 or 2, set currentStatus to both 'Active' and 'Inactive'
                    : [true];
        setSelectedStatus(newStatus);
        showGrid(filtered, newStatus)
    }

    // handle feeStage form submit (add or edit)
    // const handleFeeStageSubmit = async (feeStageFormData: any) => {
    //     if (selectedFeeStage) {
    //         setFeeStageDataList((prevList) => {
    //             const updatedList = prevList.map((feestage) =>
    //                 feestage.feeStageId === selectedFeeStage.feeStageId
    //                     ? {
    //                         ...feestage, // Keep original Feestage data
    //                         refrenceNumber: feeStageFormData.refrenceNumber,
    //                         description: feeStageFormData.description,
    //                         completionNotes: feeStageFormData.completionNotes,
    //                         value: feeStageFormData.value,
    //                         valuePer: feeStageFormData.valuePer,
    //                         status: feeStageFormData.status, // Update based on Status
    //                         updatedId: userId,
    //                     }
    //                     : feestage
    //             );
    //             showGrid(updatedList); // Send updated list to showGrid
    //             return updatedList;
    //         });
    //     }
    //     else {
    //         setFeeStageDataList((prevData) => {
    //             const maxFeeStageId = prevData.reduce((maxId, feestage) => {
    //                 return feestage.feeStageId > maxId ? feestage.feeStageId : maxId;
    //             }, 0);

    //             // Create the new contact with an incremented contactId
    //             const newFeeStage: FeeStage = {
    //                 ...feeStageFormData,
    //                 feeStageId: maxFeeStageId + 1,
    //                 status: feeStageFormData.status,
    //                 createdId: userId, // Assign the appropriate createdId as needed
    //             };

    //             // Return the updated contactData array with the new contact added
    //             const updatedData = [...prevData, newFeeStage];
    //             showGrid(updatedData)
    //             return updatedData;
    //         });
    //     }
    // }

    const handleFeeStageSubmit = async (feeStageFormData: any) => {
        let response: ApiResponse<any>;

        if (selectedFeeStage) {
            const updatedFeeStage = {
                feeStageId: selectedFeeStage.feeStageId,
                frameworkId: frameworkId,
                refrenceNumber: feeStageFormData.refrenceNumber,
                description: feeStageFormData.description,
                completionNotes: feeStageFormData.completionNotes,
                value: feeStageFormData.value,
                valuePer: feeStageFormData.valuePer,
                status: feeStageFormData.status, // Update based on Status
                updatedId: userId,
            };

            response = await execute('put', '/Framework/UpdateFeeStage', updatedFeeStage);

        } else {
            // Adding a new FeeStage
            const newFeeStage: FeeStage = {
                ...feeStageFormData,
                frameworkId: frameworkId,
                // status: feeStageFormData.status,
                createdId: userId, // Assign the appropriate createdId as needed
            };

            response = await execute('post', '/Framework/AddFeeStage', newFeeStage);
        }

        if (response!) {
            if (response.error) {
                // If there's an error in the response, display the error message
                setToast({ open: true, message: response.error, type: "error" });
            } else {
                // If the response is successful, display the success message
                setToast({ open: true, message: response.data, type: "success" });
                handleClose(false);
                handleContentChange(false);
                fetchAllFeeStages();  // Awaiting the API call
            }
        }
    }

    const handleToggleState = (row: any) => {
        if (row.Status === 'Inactive') {
            setIsOn(false)
        } else {
            setIsOn(true)
        }
        setSelectedFeeStage(row);
        setDialogOpen(true);
    }

    // edit fee stage 
    const handleEdit = (feeStage: any) => {
        // console.log(feeStage)
        setSelectedFeeStage(feeStage); // Set the contact to edit
        setOpen(true); // Open the modal
    };

    const handleDialogCancel = () => {
        setSelectedFeeStage(null); // Reset selectedUser when modal closes
        setDialogOpen(false);
    }

    const handleOpenModal = () => {
        setSelectedFeeStage(null)
        setOpen(true);
    }

    const handleDialogConfirm = async () => {
        if (!selectedFeeStage) return;

        try {
            // Make API call to update the status
            const { data } = await execute('get', `/Framework/GetFeeStage/${selectedFeeStage.feeStageId}`);
           
            if (data) {
                // Step 2: Update the fields on the filtered record
                const updatedFeeStageData = {
                    ...data, // Keep the original user data
                    status: selectedFeeStage.Status === "Active" ? false : true,
                    updatedId: userId, // You can also update any additional fields like updatedId
                };

                let response: ApiResponse<any> = await execute('put', '/Framework/UpdateFeeStage', updatedFeeStageData);

                if (response!) {
                    if (response.error) {
                        // If there's an error in the response, display the error message
                        setToast({ open: true, message: response.error, type: "error" });
                    } else {
                        // If the response is successful, display the success message
                        setToast({ open: true, message: response.data, type: "success" });
                    }
                }
            }
            fetchAllFeeStages();
            setSelectedFeeStage(null); // Clear selected row
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setDialogOpen(false);
        }

        // try {
        //     setFeeStageDataList((prevList) => {
        //         const updatedList = prevList.map((feestage) =>
        //             feestage.feeStageId === selectedFeeStage.feeStageId
        //                 ? {
        //                     ...feestage, // Keep original feestage
        //                     status: selectedFeeStage.Status === "Active" ? false : true,
        //                     updatedId: userId,
        //                 }
        //                 : feestage
        //         );
        //         showGrid(updatedList); // Send updated list to showGrid
        //         return updatedList;
        //     });
        // } finally {
        //     setDialogOpen(false);
        // }
    }

    const { ConfirmLeaveDialog } = useFormUnsavedChanges({
        formData,
        initialFormData,
        routesToBlock: majorRoutes
    });

    const handleContentChange = (state: boolean) => {
        setHasUnsavedChanges(state);
    };

    return (
        <AppLayout>
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%",minHeight: 0, overflow: "hidden" }}>
                <Grid2 container spacing={1} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Grid2 sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton sx={{ width: '40px', height: '40px', backgroundColor: 'transparent' }} onClick={() => handleNavigate()} >
                            <ArrowBackIosIcon sx={{ color: '#1e66ab', fontSize: '15px' }} />
                        </IconButton>

                        <Typography variant="h6" noWrap sx={headingStyles}
                        >
                            {frameworkId ? 'Edit Framework' : 'Add Framework'}
                        </Typography>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6, lg: 2 }}>
                        <CustomButton
                            capsule
                            sx={{
                                mt: 0,
                                mb: 2,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                            }}
                            onClick={handleFrameworkSubmit}

                        >
                            {frameworkId ? 'Update Framework' : 'Save Framework'}
                        </CustomButton>
                    </Grid2>
                </Grid2>

                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    // height: "calc(100vh - 140px)",
                    paddingRight: 2
                }}>
                    <Grid2 container spacing={2} sx={{ mb: frameworkId ? 0 : 7 ,backgroundColor: '#f7faff',border: '1px solid #e0eaf6', borderRadius: '14px', padding: 1.5, marginBottom: 1 }}>
                        <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                            <CustomTextField
                                label="Framework Name"
                                name="frameworkName"
                                value={formData.frameworkName}
                                onChange={handleChange}
                                sx={{ mb: 1,  height: '43px', backgroundColor: 'white', borderRadius: '8px',  }}
                                required={true}
                            />
                            {errors.frameworkName && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.frameworkName}
                                </Typography>
                            )}
                        </Grid2>


                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <CustomDropdown
                                value={formData.clientId}
                                onChange={(event) => setFormData({ ...formData, clientId: Number(event.target.value) })}
                                label="Associated Client"
                                options={client}
                                requiredLabel={true}
                                sx={{ mb: 1, height: '43px', backgroundColor: 'white', borderRadius: '8px', }}
                            />
                            {errors.associatedClient && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.associatedClient}
                                </Typography>
                            )}
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                            <CustomDatePicker
                                label="Start Date"
                                value={formData.startDate}
                                required={true}
                                onChange={(newValue) => setFormData({ ...formData, startDate: newValue })}
                                height= {43}
                                sx={{ mb: 1,backgroundColor: 'white', borderRadius: '8px',  }}
                            />
                            {errors.startDate && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.startDate}
                                </Typography>
                            )}
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                            <CustomDatePicker
                                label="End Date"
                                value={formData.endDate}
                                required={true}
                                onChange={(newValue) => setFormData({ ...formData, endDate: newValue })}
                                height= {43}
                                sx={{ mb: 1,backgroundColor: 'white', borderRadius: '8px',  }}
                            />
                            {errors.endDate && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.endDate}
                                </Typography>
                            )}
                        </Grid2>

                        {frameworkId && (
                            <>
                                <Grid2 size={{ xs: 12, md: 12, lg: 12 }}>
                                    <Typography sx={{ color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1, mt: 1 }}>Status</Typography>
                                    <Grid2 sx={{ display: 'flex'}}> {/* Adjust gap as needed */}
                                        {/* Custom Radio Buttons */}
                                        <CustomRadioButton
                                            label="Active"
                                            value="true"
                                            name="status"
                                            checked={formData.status === true}
                                            onChange={handleChange}
                                            required={true}
                                        />
                                        <CustomRadioButton
                                            label="Inactive"
                                            value="false"
                                            name="status"
                                            checked={formData.status === false}
                                            onChange={handleChange}
                                            required={true}
                                        />
                                    </Grid2>
                                </Grid2>
                            </>
                        )}
                    </Grid2>

                    <FilterCard {...props} handleModalOrNavigate={() => handleOpenModal()} handleSearch={handleSearchGridData} showDateRange={true} ></FilterCard>
                    <CustomAddFeeStageModal handleContentChange={handleContentChange} open={open} onClose={handleClose} onSubmit={handleFeeStageSubmit} feeStageData={selectedFeeStage}></CustomAddFeeStageModal>
                    {feeStageDataList.length > 0 && ( <Box sx={{display: "flex", flexDirection: "column", minHeight: "auto", maxHeight: "600px"}}>
                        <CustomDataTable rows={filteredData} actionText="Fee Stage" sortOptions={sortOptions} onRowEdit={handleEdit} onRowDoubleClick={handleEdit} onToggleClick={handleToggleState} isDialogOpen={dialogOpen} isToggleOn={isOn} handleCancel={handleDialogCancel} handleConfirm={handleDialogConfirm} noResultMessage={validationMessages.common_RecordNotFound("Fee Stage")}></CustomDataTable>
                        </Box>)}

                </Box>
            </Box>
            <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
            <ConfirmLeaveDialog />
        </AppLayout>
    );
}

export default AddFramework;
